import { Tabs, Tab } from 'react-bootstrap';
import React, { useState, useEffect, useMemo, useRef } from 'react';
import styled from '@emotion/styled';
import { Global, css } from '@emotion/react';

import { PaginatedQueryResult } from 'react-query';
import StockMediaTab from './StockMediaTab';
import StockMediaAction from './ActionButtons';
import { Media as MediaProps, MediaType } from '~/types/api';
import UploadMediaTab, { UploadMediaButton } from './UploadMediaTab';

const StlyedTabs = styled(Tabs)`
  ul[role='tablist'] {
    margin-bottom: 30px;
    border-bottom: 1px solid #ddd;
    li {
      a {
        font-family: Lato;
        font-size: 16px;
        font-weight: bold;
        color: #444444;
      }
    }
  }
`;

const StlyedTab = styled(Tab)`
  height: 600px;
`;

const Header = styled.h3`
  font-family: Lato;
  font-size: 26px;
  font-weight: bold;
  letter-spacing: 0.14px;
  color: #323232;
  margin: unset;
  min-height: 40px;
`;

const HeaderContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
`;

export interface MediaPagination
  extends Partial<PaginatedQueryResult<MediaProps>> {
  activePage?: number;
  lastPage?: number;
  count?: number;
  itemsCountPerPage?: number | string;
  totalItemsCount?: number | string;
}

export interface MediaPickerWithStockProps {
  selectedId?: string;
  selectedType?: MediaType;
  tags: string | string[];
  onRequestClose?: () => void;
  setMedia: (media: Partial<MediaProps>) => void;
  onItemDeleted: (media: MediaProps) => void;
}

function getTags(tags: string | (string | null)[]) {
  if (tags instanceof Array) {
    return tags.join(' ');
  }

  return tags;
}

function MediaPickerWithStock({
  selectedId,
  selectedType,
  tags,
  onRequestClose,
  setMedia,
  onItemDeleted,
}: MediaPickerWithStockProps) {
  const [tabKey, setTabKey] = useState<string>('stock-images');

  const [preSelected, setPreSelected] = useState<Partial<MediaProps>>({
    id: selectedId,
    type: selectedType,
  });

  const isDisplayUpload = useMemo(() => tabKey === 'uploads', [tabKey]);

  const isStockMediaSelected = MediaType.Stock === selectedType;

  const fileInput = useRef<HTMLInputElement>();

  useEffect(() => {
    if (isStockMediaSelected) {
      setTabKey('stock-images');
    } else {
      setTabKey('uploads');
    }
  }, []);

  const saveMedia = useMemo(() => {
    return () => setMedia(preSelected);
  }, [preSelected]);

  const handleItemDeleted = (media: MediaProps) => {
    setPreSelected({ id: '', url: '' });
    onItemDeleted(media);
  };

  return (
    <React.Fragment>
      <HeaderContainer>
        <Header>{'Choose a Background '}</Header>
        {isDisplayUpload && <UploadMediaButton fileInput={fileInput} />}
      </HeaderContainer>
      <StlyedTabs
        id='background-media-library'
        activeKey={tabKey}
        onSelect={(k) => setTabKey(k)}
      >
        <StlyedTab eventKey='uploads' title='Upload your own images'>
          <UploadMediaTab
            tags={getTags(tags)}
            selected={preSelected}
            setSelected={setPreSelected}
            onItemDeleted={handleItemDeleted}
            fileInput={fileInput}
          />
        </StlyedTab>
        <StlyedTab eventKey='stock-images' title='Stock images'>
          <StockMediaTab
            tags={getTags(tags)}
            selected={preSelected}
            setSelected={setPreSelected}
          />
        </StlyedTab>
      </StlyedTabs>
      <StockMediaAction onRequestClose={onRequestClose} saveMedia={saveMedia} />
      <Global
        styles={css`
          .ReactModal__Content.background-media-upload {
            max-width: 95%;
            width: 1100px;
            padding: unset;
          }
        `}
      />
    </React.Fragment>
  );
}

export default MediaPickerWithStock;
